import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

// ── Formatlayıcı ────────────────────────────────────────────────────────────
const formatTakipKodu = (val) => {
  const temiz = val.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 11);
  let r = temiz.slice(0, 3);
  if (temiz.length > 3) r += '-' + temiz.slice(3, 7);
  if (temiz.length > 7) r += '-' + temiz.slice(7, 11);
  return r;
};

// ── Yardımcılar ─────────────────────────────────────────────────────────────
const tarihFormat = (t) =>
  t ? new Date(t).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const saatFormat = (t) =>
  t ? new Date(t).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '—';

const bugunBaslangic = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

// ── Modal kapsayıcı (ortalanmış) ────────────────────────────────────────────
function ModalKapsayici({ children, onKapat }) {
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onKapat(); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: '16px',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: '16px',
        width: '100%', maxWidth: '480px',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
      }}>
        {children}
      </div>
    </div>
  );
}

// ── Ana bileşen ──────────────────────────────────────────────────────────────
export default function DagitimciPanel({ onCikis }) {
  const [kullanici]          = useState(() => JSON.parse(localStorage.getItem('kullanici') || 'null'));
  const [sekme, setSekme]    = useState('bekleyen');

  const [bekleyenler, setBekleyenler] = useState([]);
  const [teslimler, setTeslimler]     = useState([]);
  const [yukleniyor, setYukleniyor]   = useState(false);

  // Teslim onay modali
  const [modal, setModal]       = useState(null);
  const [takipKodu, setTakipKodu] = useState('');
  const [teslimKodu, setTeslimKodu] = useState('');
  const [kodModu, setKodModu]   = useState('takip'); // 'takip' | 'teslim'
  const [mesaj, setMesaj]       = useState(null);
  const [isleniyor, setIsleniyor] = useState(false);

  // ── Veri çekme ────────────────────────────────────────────────────────────
  const bekleyenleriGetir = useCallback(async () => {
    setYukleniyor(true);

    // Bu dağıtımcıya atanmış seferleri al
    const { data: atananSeferler } = await supabase
      .from('seferler')
      .select('id')
      .eq('dagitimci_id', kullanici?.id);

    if (!atananSeferler || atananSeferler.length === 0) {
      setBekleyenler([]);
      setYukleniyor(false);
      return;
    }

    const seferIdler = atananSeferler.map(s => s.id);

    const { data, error } = await supabase
      .from('depo')
      .select('*, musteriler(id, ad, sehir)')
      .in('sefer_id', seferIdler)
      .neq('durum', 'Teslim Edildi')
      .order('created_at', { ascending: true });

    if (!error) setBekleyenler(data || []);
    setYukleniyor(false);
  }, [kullanici?.id]);

  const teslimleriGetir = useCallback(async () => {
    const { data, error } = await supabase
      .from('depo')
      .select('*, musteriler(id, ad)')
      .eq('durum', 'Teslim Edildi')
      .gte('teslim_tarihi', bugunBaslangic())
      .order('teslim_tarihi', { ascending: false });

    if (!error) setTeslimler(data || []);
  }, []);

  // ── 3 günden eski teslim edilmiş kayıtları sil ───────────────────────────
  const eskiTeslimleriTemizle = useCallback(async () => {
    const ucGunOnce = new Date();
    ucGunOnce.setDate(ucGunOnce.getDate() - 3);
    await supabase
      .from('depo')
      .delete()
      .eq('durum', 'Teslim Edildi')
      .lt('teslim_tarihi', ucGunOnce.toISOString());
  }, []);

  useEffect(() => {
    eskiTeslimleriTemizle();   // önce temizle, sonra listele
    bekleyenleriGetir();
    teslimleriGetir();
  }, [eskiTeslimleriTemizle, bekleyenleriGetir, teslimleriGetir]);

  // ── Teslim Et butonu: modal aç ────────────────────────────────────────────
  const teslimEtAc = (mal) => {
    setModal(mal);
    setTakipKodu('');
    setTeslimKodu('');
    setKodModu('teslim');
    setMesaj(null);
  };

  // ── Onaylama mantığı (kargo bazlı takip kodu) ────────────────────────────
  const onayla = async () => {
    if (!takipKodu.trim()) {
      setMesaj({ tip: 'hata', metin: '✗ Takip kodunu girin.' });
      return;
    }
    setIsleniyor(true);
    setMesaj(null);

    const kod = takipKodu.trim().toUpperCase();

    // 1) depo tablosunda takip_kodu ara
    const { data: depoArr } = await supabase
      .from('depo')
      .select('id, musteri_id, sefer_id, kod_gecerli')
      .eq('takip_kodu', kod)
      .limit(1);

    if (!depoArr || depoArr.length === 0) {
      setMesaj({ tip: 'hata', metin: '✗ Geçersiz takip kodu.' });
      setIsleniyor(false);
      return;
    }

    const kayit = depoArr[0];

    // 2) Bu kod bu kargoya mı ait?
    if (kayit.id !== modal.id) {
      setMesaj({ tip: 'hata', metin: '✗ Bu kod bu kargoya ait değil.' });
      setIsleniyor(false);
      return;
    }

    // 3) Kod geçerli mi?
    if (!kayit.kod_gecerli) {
      setMesaj({ tip: 'uyari', metin: '⚠ Bu kargo zaten teslim edilmiştir.' });
      setIsleniyor(false);
      return;
    }

    // 4) Teslim et + kodu geçersiz yap
    const { error: depErr } = await supabase
      .from('depo')
      .update({ durum: 'Teslim Edildi', teslim_tarihi: new Date().toISOString(), kod_gecerli: false })
      .eq('id', modal.id);

    if (depErr) {
      setMesaj({ tip: 'hata', metin: '✗ Güncelleme hatası: ' + depErr.message });
      setIsleniyor(false);
      return;
    }

    // 5) Seferdeki tüm mallar teslim mi? → sefer durumunu güncelle
    if (modal.sefer_id) {
      const { data: kalanlar } = await supabase
        .from('depo')
        .select('id, durum')
        .eq('sefer_id', modal.sefer_id)
        .neq('id', modal.id)
        .neq('durum', 'Teslim Edildi');

      if (!kalanlar || kalanlar.length === 0) {
        await supabase
          .from('seferler')
          .update({ durum: 'Teslim Edildi' })
          .eq('id', modal.sefer_id);
      }
    }

    setMesaj({ tip: 'ok', metin: '✓ Teslim edildi!' });
    setIsleniyor(false);

    setTimeout(() => {
      setModal(null);
      setTakipKodu('');
      setMesaj(null);
      bekleyenleriGetir();
      teslimleriGetir();
    }, 1500);
  };

  const teslimKoduIleOnayla = async () => {
    const kod = teslimKodu.trim();
    if (!kod || kod.length !== 4) {
      setMesaj({ tip: 'hata', metin: '✗ 4 rakamlı teslim kodunu girin.' });
      return;
    }
    setIsleniyor(true);
    setMesaj(null);

    const { data: depoArr } = await supabase
      .from('depo')
      .select('id, musteri_id, sefer_id, kod_gecerli, teslim_kodu')
      .eq('teslim_kodu', kod)
      .limit(1);

    if (!depoArr || depoArr.length === 0) {
      setMesaj({ tip: 'hata', metin: '✗ Geçersiz teslim kodu.' });
      setIsleniyor(false);
      return;
    }

    const kayit = depoArr[0];
    if (kayit.id !== modal.id) {
      setMesaj({ tip: 'hata', metin: '✗ Bu kod bu kargoya ait değil.' });
      setIsleniyor(false);
      return;
    }
    if (!kayit.kod_gecerli) {
      setMesaj({ tip: 'uyari', metin: '⚠ Bu kargo zaten teslim edilmiştir.' });
      setIsleniyor(false);
      return;
    }

    const { error: depErr } = await supabase
      .from('depo')
      .update({ durum: 'Teslim Edildi', teslim_tarihi: new Date().toISOString(), kod_gecerli: false })
      .eq('id', modal.id);

    if (depErr) {
      setMesaj({ tip: 'hata', metin: '✗ Güncelleme hatası: ' + depErr.message });
      setIsleniyor(false);
      return;
    }

    if (modal.sefer_id) {
      const { data: kalanlar } = await supabase
        .from('depo').select('id, durum')
        .eq('sefer_id', modal.sefer_id).neq('id', modal.id).neq('durum', 'Teslim Edildi');
      if (!kalanlar || kalanlar.length === 0) {
        await supabase.from('seferler').update({ durum: 'Teslim Edildi' }).eq('id', modal.sefer_id);
      }
    }

    setMesaj({ tip: 'ok', metin: '✓ Teslim edildi!' });
    setIsleniyor(false);
    setTimeout(() => {
      setModal(null); setTeslimKodu(''); setTakipKodu(''); setMesaj(null);
      bekleyenleriGetir(); teslimleriGetir();
    }, 1500);
  };

  // ── Stiller ───────────────────────────────────────────────────────────────
  const s = {
    kart: {
      background: '#fff', borderRadius: '12px',
      padding: '16px', marginBottom: '12px',
      boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
      border: '1px solid #e8ecf0',
    },
    etiket: {
      fontSize: '11px', color: '#6B7280',
      textTransform: 'uppercase', fontWeight: '600',
      letterSpacing: '0.5px', marginBottom: '2px',
    },
    deger: { fontSize: '14px', color: '#1F2937', marginBottom: '10px' },
    badge: (renk, bg) => ({
      display: 'inline-block', padding: '3px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '600',
      background: bg, color: renk,
    }),
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#F3F6FA', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: '#1a3a6b', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', height: '56px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}>
        <div>
          <img src="/logo.png" alt="BYR SWORD" style={{ height: '36px', objectFit: 'contain', borderRadius: '4px' }} />
          <span style={{ fontSize: '12px', color: '#c5d5e8', marginLeft: '10px' }}>🚚 Dağıtım</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: '#c5d5e8' }}>{kullanici?.ad}</span>
          <button
            onClick={onCikis}
            style={{
              padding: '6px 12px', background: 'rgba(255,255,255,0.12)',
              color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px', cursor: 'pointer', fontSize: '12px',
            }}
          >
            Çıkış Yap
          </button>
        </div>
      </div>

      {/* ── Sekme Barı ── */}
      <div style={{
        display: 'flex', background: '#fff',
        borderBottom: '1px solid #e5e9f0',
        position: 'sticky', top: '56px', zIndex: 90,
      }}>
        {[
          { key: 'bekleyen', label: `📦 Bekleyen (${bekleyenler.length})` },
          { key: 'teslim',   label: `✅ Bugün Teslim (${teslimler.length})` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setSekme(tab.key)}
            style={{
              flex: 1, padding: '14px 8px',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: sekme === tab.key ? '700' : '500',
              color: sekme === tab.key ? '#1a3a6b' : '#6B7280',
              borderBottom: sekme === tab.key ? '3px solid #1a3a6b' : '3px solid transparent',
              transition: 'all 0.2s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── İçerik ── */}
      <div style={{ padding: '16px', maxWidth: '640px', margin: '0 auto' }}>

        {yukleniyor && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            Yükleniyor…
          </div>
        )}

        {/* Bekleyen Teslimatlar */}
        {!yukleniyor && sekme === 'bekleyen' && (
          <>
            {bekleyenler.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '60px 20px',
                color: '#9CA3AF', fontSize: '15px',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
                Tüm teslimatlar tamamlandı!
              </div>
            ) : (
              bekleyenler.map(mal => (
                <div key={mal.id} style={s.kart}>
                  {/* Müşteri adı */}
                  <div style={{ fontSize: '17px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>
                    👤 {mal.musteriler?.ad || '—'}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                    <div>
                      <div style={s.etiket}>Ürün</div>
                      <div style={s.deger}>{mal.urun || '—'}</div>
                    </div>
                    <div>
                      <div style={s.etiket}>Miktar</div>
                      <div style={s.deger}>{mal.miktar ? `${mal.miktar} ${mal.birim || ''}` : '—'}</div>
                    </div>
                    <div>
                      <div style={s.etiket}>Hedef Ülke</div>
                      <div style={s.deger}>{mal.hedef_ulke || '—'}</div>
                    </div>
                    <div>
                      <div style={s.etiket}>Şehir</div>
                      <div style={s.deger}>{mal.musteriler?.sehir || '—'}</div>
                    </div>
                    <div>
                      <div style={s.etiket}>Barkod No</div>
                      <div style={{ ...s.deger, fontFamily: 'monospace', fontSize: '13px' }}>
                        {mal.barkod || '—'}
                      </div>
                    </div>
                  </div>

                  {/* Durum badge */}
                  <div style={{ marginBottom: '14px' }}>
                    <span style={s.badge('#854F0B', '#FEF3C7')}>{mal.durum || 'Depoda'}</span>
                  </div>

                  {/* Teslim Et butonu */}
                  <button
                    onClick={() => teslimEtAc(mal)}
                    style={{
                      width: '100%', padding: '13px',
                      background: '#16A34A', color: '#fff',
                      border: 'none', borderRadius: '10px',
                      fontSize: '15px', fontWeight: '700',
                      cursor: 'pointer', letterSpacing: '0.3px',
                    }}
                  >
                    🚚 Teslim Et
                  </button>
                </div>
              ))
            )}
          </>
        )}

        {/* Bugün Teslim Edilenler */}
        {!yukleniyor && sekme === 'teslim' && (
          <>
            {teslimler.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '60px 20px',
                color: '#9CA3AF', fontSize: '15px',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
                Bugün henüz teslim yapılmadı.
              </div>
            ) : (
              teslimler.map(mal => (
                <div key={mal.id} style={{
                  ...s.kart,
                  borderLeft: '4px solid #16A34A',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '22px' }}>✅</span>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                        {mal.musteriler?.ad || '—'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        {tarihFormat(mal.teslim_tarihi)} — {saatFormat(mal.teslim_tarihi)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                    <div>
                      <div style={s.etiket}>Ürün</div>
                      <div style={s.deger}>{mal.urun || '—'}</div>
                    </div>
                    <div>
                      <div style={s.etiket}>Miktar</div>
                      <div style={s.deger}>{mal.miktar ? `${mal.miktar} ${mal.birim || ''}` : '—'}</div>
                    </div>
                    <div>
                      <div style={s.etiket}>Hedef Ülke</div>
                      <div style={s.deger}>{mal.hedef_ulke || '—'}</div>
                    </div>
                    <div>
                      <div style={s.etiket}>Barkod</div>
                      <div style={{ ...s.deger, fontFamily: 'monospace', fontSize: '13px' }}>
                        {mal.barkod || '—'}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {/* ── Teslim Onay Modali ── */}
      {modal && (
        <ModalKapsayici onKapat={() => { setModal(null); setMesaj(null); setTakipKodu(''); setTeslimKodu(''); }}>
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: '#111827' }}>Teslimatı Onayla</h2>
              <button onClick={() => { setModal(null); setMesaj(null); setTakipKodu(''); setTeslimKodu(''); }}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B7280' }}>✕</button>
            </div>

            <div style={{ background: '#F0F9FF', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', border: '1px solid #BAE6FD' }}>
              <div style={{ fontWeight: '700', fontSize: '15px', color: '#0C4A6E', marginBottom: '6px' }}>
                👤 {modal.musteriler?.ad || '—'}
              </div>
              <div style={{ fontSize: '13px', color: '#374151' }}>
                <span style={{ marginRight: '14px' }}>📦 {modal.urun || '—'}</span>
                <span>{modal.miktar} {modal.birim}</span>
              </div>
              {modal.hedef_ulke && <div style={{ fontSize: '13px', color: '#374151', marginTop: '4px' }}>🌍 {modal.hedef_ulke}</div>}
              {modal.musteriler?.sehir && <div style={{ fontSize: '13px', color: '#374151', marginTop: '4px' }}>📍 {modal.musteriler.sehir}</div>}
            </div>

            {/* Kod modu toggle */}
            <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', borderRadius: '8px', padding: '3px', marginBottom: '14px' }}>
              {[{ key: 'teslim', label: '🔢 Teslim Kodu' }, { key: 'takip', label: '🔑 Takip Kodu' }].map(m => (
                <button key={m.key} onClick={() => { setKodModu(m.key); setMesaj(null); }}
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: kodModu === m.key ? '700' : '400', background: kodModu === m.key ? '#0D3B7A' : 'transparent', color: kodModu === m.key ? '#fff' : '#6B7280', transition: 'all 0.2s' }}>
                  {m.label}
                </button>
              ))}
            </div>

            {kodModu === 'teslim' ? (
              <>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  4 Rakamlı Teslim Kodu
                </label>
                <input
                  value={teslimKodu}
                  onChange={e => { setTeslimKodu(e.target.value.replace(/\D/g, '').slice(0, 4)); setMesaj(null); }}
                  onKeyDown={e => e.key === 'Enter' && teslimKoduIleOnayla()}
                  placeholder="1234"
                  inputMode="numeric"
                  style={{
                    width: '100%', padding: '14px', border: '2px solid #D1D5DB', borderRadius: '10px',
                    fontSize: '28px', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box',
                    textAlign: 'center', letterSpacing: '8px', fontWeight: '800',
                    borderColor: mesaj?.tip === 'hata' ? '#EF4444' : mesaj?.tip === 'ok' ? '#16A34A' : '#D1D5DB',
                  }}
                />
              </>
            ) : (
              <>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Müşteri Takip Kodunu Girin
                </label>
                <input
                  value={takipKodu}
                  onChange={e => { setTakipKodu(formatTakipKodu(e.target.value)); setMesaj(null); }}
                  onKeyDown={e => e.key === 'Enter' && onayla()}
                  placeholder="BYR-1234-ABCD"
                  style={{
                    width: '100%', padding: '12px 14px', border: '2px solid #D1D5DB', borderRadius: '10px',
                    fontSize: '16px', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box', letterSpacing: '1px',
                    borderColor: mesaj?.tip === 'hata' ? '#EF4444' : mesaj?.tip === 'ok' ? '#16A34A' : '#D1D5DB',
                  }}
                />
              </>
            )}

            {mesaj && (
              <div style={{
                marginTop: '10px', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                background: mesaj.tip === 'ok' ? '#DCFCE7' : mesaj.tip === 'hata' ? '#FEE2E2' : '#FEF3C7',
                color: mesaj.tip === 'ok' ? '#16A34A' : mesaj.tip === 'hata' ? '#DC2626' : '#92400E',
              }}>
                {mesaj.metin}
              </div>
            )}

            {mesaj?.tip !== 'ok' && (
              <button
                onClick={kodModu === 'teslim' ? teslimKoduIleOnayla : onayla}
                disabled={isleniyor}
                style={{
                  width: '100%', marginTop: '16px', padding: '14px',
                  background: isleniyor ? '#9CA3AF' : '#16A34A',
                  color: '#fff', border: 'none', borderRadius: '10px',
                  fontSize: '15px', fontWeight: '700', cursor: isleniyor ? 'default' : 'pointer',
                }}>
                {isleniyor ? 'Kaydediliyor…' : '✓ Onayla'}
              </button>
            )}
          </div>
        </ModalKapsayici>
      )}
    </div>
  );
}
